/**
 * SRES · Grade-Shell Isosurface Layer — Phase 1 · Feb 2026.
 *
 * Fetches a marching-cubes mesh from
 *     GET /api/projects/{pid}/block-model/isosurface?cutoff=X
 * and renders it as a translucent gold shell inside the Cockpit's
 * Three.js scene.
 *
 * The mesh is re-fetched (with debouncing via react-query) whenever
 * the cutoff or project changes.  We keep the shell in a `useRef`
 * `BufferGeometry` so React re-renders don't recreate GPU buffers on
 * every hover event in the parent scene.
 */
import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import * as THREE from "three";

import { api } from "@/lib/api";
import { worldToScene } from "@/lib/sceneCoords";

const GOLD_DEEP = "#B8874A";

export default function GradeShellMesh({
  pid,
  cutoff,
  origin,
  verticalExaggeration = 1,
  visible = true,
  opacity = 0.55,
}) {
  const q = useQuery({
    queryKey: ["cockpit-isosurface", pid, Number(cutoff).toFixed(3)],
    queryFn: () =>
      api
        .get(`/projects/${pid}/block-model/isosurface`, {
          params: { cutoff: Number(cutoff) },
        })
        .then((r) => r.data),
    enabled: !!pid && visible && Number.isFinite(cutoff),
    // Cutoff sliders can fire fast — 250 ms staleness dampens over-fetching.
    staleTime: 15_000,
    keepPreviousData: true,
  });

  // Build the BufferGeometry once per successful fetch.
  const geometry = useMemo(() => {
    const data = q.data;
    if (!data || !data.vertices?.length || !data.indices?.length) return null;
    if (!origin) return null;

    const geom = new THREE.BufferGeometry();
    const n = data.vertices.length;
    const flatVerts = new Float32Array(n * 3);
    // Vertices come from the marching-cubes engine in world (UTM) space
    // as [x=Easting, y=Northing, z=Elevation]. Convert each into the
    // scene's local frame using the same helper as every other layer.
    for (let i = 0; i < n; i++) {
      const [e, no, z] = data.vertices[i];
      const p = worldToScene(e, no, z, origin, verticalExaggeration);
      flatVerts[i * 3]     = p.x;
      flatVerts[i * 3 + 1] = p.y;
      flatVerts[i * 3 + 2] = p.z;
    }
    geom.setAttribute("position", new THREE.BufferAttribute(flatVerts, 3));

    const idx =
      data.indices.length > 65535
        ? new Uint32Array(data.indices)
        : new Uint16Array(data.indices);
    geom.setIndex(new THREE.BufferAttribute(idx, 1));

    // Always recompute normals in scene space (source normals live in
    // world space so they'd be wrong after axis flip).
    geom.computeVertexNormals();
    geom.computeBoundingSphere();
    return geom;
  }, [q.data, origin, verticalExaggeration]);

  const meshRef = useRef();

  useEffect(() => {
    return () => {
      // Free GPU memory when unmounting.
      if (geometry) geometry.dispose();
    };
  }, [geometry]);

  if (!visible || !geometry || !origin) return null;

  // Vertices are already in the local scene frame; no additional
  // transform needed.
  return (
    <mesh
      ref={meshRef}
      geometry={geometry}
      renderOrder={2}
      data-testid="cockpit-grade-shell"
    >
      <meshPhysicalMaterial
        color={GOLD_DEEP}
        emissive={GOLD_DEEP}
        emissiveIntensity={0.06}
        metalness={0.65}
        roughness={0.35}
        transparent
        opacity={opacity}
        depthWrite={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}
